import { useCallback, useEffect, useState } from "react";
import { MatchStatsView, type MatchStatsData, type MatchStatsPeriod } from "@dolfin/ui";
import { api } from "@/api/client";

// Client rollup without an organisation, single-org drill-down with one (same shape). The org goes in
// x-dolfin-organisation-id; a Member with no UserOrganisation row gets a 403.
export function MatchStatsPanel({ organisationId }: { organisationId?: string }) {
  const [data, setData] = useState<MatchStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<MatchStatsPeriod>("Last30Days");

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const request = organisationId
      ? api.GET("/v1/match-stats", {
          params: { query: { period } },
          headers: { "x-dolfin-organisation-id": organisationId },
        })
      : api.GET("/v1/client/match-stats", { params: { query: { period } } });

    request
      .then(({ data: body, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !body) {
          setError("The match statistics could not be loaded. You may not have access to this organisation.");
          return;
        }
        setData(body as MatchStatsData);
      })
      .catch(() => {
        if (!cancelled) setError("The match statistics could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organisationId, period]);

  useEffect(() => load(), [load]);

  return (
    <MatchStatsView
      data={data}
      loading={loading}
      error={error}
      period={period}
      onPeriodChange={setPeriod}
      onRetry={load}
    />
  );
}
